/* eslint-disable prefer-destructuring */
import {
  Box,
  Divider,
  ErrorfilledIcon,
  FormControl,
  FormControlLabel,
  GKButton,
  GKCheckbox,
  GKDatePicker,
  GKRadio,
  GKSearchSelect,
  GKSelect,
  Grid2,
  IconButton,
  MenuItem,
  NoData,
  RadioGroup,
  Show,
  Switch,
  Tooltip,
  Typography,
  ViewPdf,
} from "components";
import dayjs from "dayjs";
import useReport from "hooks/useReport";
import { isArray, isEmpty } from "lodash";
import { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useAppSelector } from "store/store";
import { CUSTOM_REPORT_RETURN_METRICS } from "utils/constants";
import CustomReportForm from "./CustomReportForm";
import CustomReportPreview from "./CustomReportPreview";
import TemplateModal from "./TemplateModal";

interface Props {
  selectedIndex: {
    index: number;
    loading: boolean;
  };
  setSelectedIndex: any;
  quoteList: any;
  benchmarkList: any;
  benchmarkLoading: boolean;
  setEditQuoteValue: any;
  setOpenQuoteModal: (openQuoteModal: boolean) => void;
  equityBasketData: any;
}

enum PeriodOptions {
  inception = "inception",
  lastFY = "lastFY",
  custom = "custom",
  thisFY = "thisFY",
}

const GenerateReportForm = (props: Props) => {
  const form = useFormContext();
  const { handleDeleteQuote, generateQuote, portfolioList, isInitialLoading } =
    useReport();

  const companies = useAppSelector((state) => state?.app?.companies);

  const [openPdfView, setOpenPdfView] = useState(false);
  const [openTemplateModal, setOpenTemplateModal] = useState(false);

  const {
    selectedIndex,
    setSelectedIndex,
    quoteList,
    benchmarkList,
    benchmarkLoading,
    setEditQuoteValue,
    setOpenQuoteModal,
    equityBasketData,
  } = props;

  const { setValue, getValues } = form;

  const equityBasketOptions =
    equityBasketData?.data?.data?.map((item: any) => ({
      id: item.id,
      name: item.name,
    })) || [];

  const MFOption = [
    {
      name: "Equity",
      value: "EQ",
    },
    {
      name: "Mutual Fund",
      value: "MF",
    },
    {
      name: "Private Equity",
      value: "PEQ",
    },
    {
      name: "Fixed Deposit",
      value: "FD",
    },
    {
      name: "Alternative Investment Funds",
      value: "AIF",
    },
  ];

  const MFOptionList = MFOption?.filter(
    (data: any) =>
      !form
        .getValues("instType")
        ?.map((dataItem: any) => dataItem)
        .includes(data.value),
  );

  const handleClickPeriods = (val: string) => {
    setValue("currentOption", val);

    const currentDate = dayjs();
    const financialYearStartMonth = 3; // April (0-indexed, so 3 is April)
    const financialYearStartDay = 1; // 1st day of the month

    const getFinancialYearDates = (isLastFY = false) => {
      const isBeforeFYStart = currentDate.isBefore(
        dayjs(
          new Date(
            currentDate.year(),
            financialYearStartMonth,
            financialYearStartDay,
          ),
        ),
      );

      let fromDate;
      let toDate;

      if (isBeforeFYStart) {
        fromDate = dayjs(
          new Date(
            currentDate.year() - (isLastFY ? 2 : 1),
            financialYearStartMonth,
            financialYearStartDay,
          ),
        );
        toDate = dayjs(
          new Date(
            currentDate.year() - (isLastFY ? 1 : 0),
            financialYearStartMonth,
            financialYearStartDay,
          ),
        ).subtract(1, "day");
      } else {
        fromDate = dayjs(
          new Date(
            currentDate.year() - (isLastFY ? 1 : 0),
            financialYearStartMonth,
            financialYearStartDay,
          ),
        );
        toDate = dayjs(
          new Date(
            currentDate.year() + (isLastFY ? 0 : 1),
            financialYearStartMonth,
            financialYearStartDay,
          ),
        ).subtract(1, "day");
      }

      return {
        fromDate: fromDate.format("YYYY-MM-DD"),
        toDate: toDate.format("YYYY-MM-DD"),
      };
    };

    switch (val) {
      case PeriodOptions.inception:
        setValue("from_date", "1990-01-01");
        setValue("to_date", currentDate.format("YYYY-MM-DD"));
        break;

      case PeriodOptions.lastFY: {
        const { fromDate, toDate } = getFinancialYearDates(true);
        setValue("from_date", fromDate);
        setValue("to_date", toDate);
        break;
      }

      case PeriodOptions.thisFY: {
        const { fromDate, toDate } = getFinancialYearDates();
        setValue("from_date", fromDate);
        setValue("to_date", toDate);
        break;
      }

      case PeriodOptions.custom:
        setValue("from_date", "");
        setValue("to_date", "");
        break;

      default:
        break;
    }
  };

  const selectedReport = getValues("selectedReported");

  const notAccessThisSectionReport = ![
    "custom_report",
    "portfolio_performance_report",
    "portfolio_summary_detailed_report",
    "xirr_report",
    "unrealized_capital_gain_summary_report",
    "unrealized_capital_gain_detailed_report",
    "historical_valuation_report",
    "stock_wise_holdings_report",
    "benchmark_performance_report",
    "year_on_year_performance_report",
    "gst_report",
  ].includes(selectedReport?.reportName);

  const notAccessMFOption = [
    "stock_wise_holdings_report",
    "income_report",
    "xirr_report",
    "portfolio_performance_report",
    "custom_report",
    "aum_growth_report",
    "benchmark_performance_report",
    "expense_report",
    "year_on_year_performance_report",
  ];

  const FORMAT_ALLOWED_REPORTS = [
    "historical_valuation_report",
    "xirr_report",
    "portfolio_summary_detailed_report",
    "holding_period_report",
    "transaction_report",
    "unrealized_capital_gain_detailed_report",
    "unrealized_capital_gain_summary_report",
    "capital_gain_detailed_report",
    "custom_report",
  ];

  return (
    <>
      <Show.When isTrue={isEmpty(selectedReport)}>
        <NoData
          customImg={
            <img loading="lazy" src="/LandingPage/noReport.svg" alt="" />
          }
          text="Report Preview"
          subText="Please select the report type for see the preview of report"
        />
      </Show.When>
      <Show.When isTrue={!isEmpty(selectedReport)}>
        <Grid2 container spacing={1.5}>
          <Grid2 size={12}>
            <Typography
              fontWeight={600}
              fontSize={18}
              textTransform={"capitalize"}
            >
              {selectedReport?.name}
            </Typography>
            <Typography>{selectedReport?.description}</Typography>
          </Grid2>
          <Grid2 size={12}>
            <Divider />
          </Grid2>
          {selectedReport?.reportName !== "stock_wise_holdings_report" &&
            selectedReport?.reportName !== "benchmark_performance_report" &&
            selectedReport?.reportName !== "gst_report" &&
            (selectedReport?.reportName !== "custom_report" ||
              selectedReport?.reportType === "template") && (
              <Grid2 size={12}>
                <Controller
                  name="portfolio"
                  control={form.control}
                  render={({ field, formState: { errors } }) => (
                    <GKSearchSelect
                      {...field}
                      disabled={
                        selectedIndex.loading || form.getValues("allPortfolio")
                      }
                      inputLabel={(() => {
                        const reportName = form.getValues(
                          "selectedReported.reportName",
                        );
                        const selectType = form.getValues(
                          "selectedReported.customData.selectType",
                        );

                        if (reportName === "custom_report") {
                          if (selectType === "portfolio") return "Portfolio";
                          if (selectType === "client") return "Client";
                          if (selectType === "family") return "Family";
                        }

                        return "Portfolio, Client or Family";
                      })()}
                      multiple
                      loading={isInitialLoading}
                      onChange={(event, val) => {
                        field.onChange(val);
                      }}
                      groupBy={(option: any) => option.type?.toUpperCase()}
                      renderOption={(props, option) => {
                        return (
                          <li
                            {...props}
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                            }}
                          >
                            <Typography fontWeight={600}>
                              {option.clientName}
                            </Typography>
                            <Typography>{option.name}</Typography>
                          </li>
                        );
                      }}
                      options={
                        portfolioList.data
                          ?.filter((item: { type: string }) => {
                            const reportName = form.getValues(
                              "selectedReported.reportName",
                            );
                            const selectType = form.getValues(
                              "selectedReported.customData.selectType",
                            );

                            // Only filter when reportName is custom_report
                            if (reportName === "custom_report") {
                              if (selectType === "portfolio") {
                                return item?.type === "portfolio";
                              }
                              if (selectType === "client") {
                                return item?.type === "client";
                              }
                              if (selectType === "family") {
                                return item?.type === "family";
                              }
                            }

                            return item;
                          })
                          ?.map((dataItem: any) => {
                            return {
                              ...dataItem,
                              name: `${dataItem.name} - ${dataItem.code}`,
                              value: dataItem.id,
                            };
                          }) || []
                      }
                      getOptionLabel={(option) => option.name || option}
                      error={!!errors?.portfolio}
                      helperText={errors?.portfolio?.message as string}
                    />
                  )}
                />
              </Grid2>
            )}

          {selectedReport?.reportName === "aum_growth_report" && (
            <>
              <Grid2 size={12} display="flex" alignItems="center" gap={2}>
                <Controller
                  name="allPortfolio"
                  control={form.control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <GKCheckbox
                          disabled={
                            (form.getValues("portfolio") ?? []).length === 1
                          }
                          checked={field.value}
                          disableLeftPadding
                          onChange={(event) => {
                            field.onChange(event.target.checked);
                            form.trigger("portfolio");
                          }}
                        />
                      }
                      label="Include All Portfolios"
                    />
                  )}
                />
                <Grid2 size={0.8} display="flex" alignItems="center">
                  <Divider orientation="vertical" sx={{ height: "20px" }} />
                </Grid2>
                <Controller
                  control={form.control}
                  name="withCash"
                  render={({ field }) => (
                    <Box display="flex" alignItems="center" height="100%">
                      <Typography fontSize={14} mr={2}>
                        Without Cash
                      </Typography>

                      <Switch
                        checked={field.value}
                        onChange={(event) =>
                          field.onChange(event.target.checked)
                        }
                      />

                      <Typography fontSize={14} ml={0.5}>
                        With Cash
                      </Typography>
                    </Box>
                  )}
                />
              </Grid2>
            </>
          )}

          {selectedReport?.reportType !== "template" &&
            selectedReport?.reportName !== "gst_report" &&
            !notAccessMFOption.includes(selectedReport?.reportName) && (
              <Grid2 size={12}>
                <Controller
                  name="instType"
                  control={form.control}
                  render={({ field, formState: { errors } }) => (
                    <GKSearchSelect
                      {...field}
                      multiple
                      inputLabel="Select Asset Class"
                      value={
                        MFOption?.filter((option) =>
                          field.value?.includes(option.value),
                        ) || []
                      }
                      onChange={(event, val) => {
                        const selectedIds = val.map(
                          (option: any) => option.value,
                        );
                        field.onChange(selectedIds);
                      }}
                      options={MFOptionList}
                      getOptionLabel={(option) => option.name || option}
                      error={!!errors?.portfolio}
                      helperText={errors?.portfolio?.message as string}
                    />
                  )}
                />
              </Grid2>
            )}

          {notAccessThisSectionReport && (
            <>
              <Controller
                name="currentOption"
                control={form.control}
                render={({ field }) => (
                  <Grid2 container size={12} display={"flex"} spacing={1}>
                    <Grid2 size={12}>
                      <Typography color={"var(--black)"}>
                        Available Time Periods
                      </Typography>
                    </Grid2>
                    <Grid2 size={3}>
                      <GKButton
                        style={{
                          height: "100%",
                        }}
                        fullWidth
                        variant={
                          field.value === "inception" ? "contained" : "outlined"
                        }
                        onClick={() => handleClickPeriods("inception")}
                      >
                        Since Inception
                      </GKButton>
                    </Grid2>
                    <Grid2 size={3}>
                      <GKButton
                        style={{
                          height: "100%",
                        }}
                        fullWidth
                        variant={
                          field.value === "thisFY" ? "contained" : "outlined"
                        }
                        onClick={() => handleClickPeriods("thisFY")}
                      >
                        This FY
                      </GKButton>
                    </Grid2>
                    <Grid2 size={3}>
                      <GKButton
                        style={{
                          height: "100%",
                        }}
                        fullWidth
                        variant={
                          field.value === "lastFY" ? "contained" : "outlined"
                        }
                        onClick={() => handleClickPeriods("lastFY")}
                      >
                        Previous FY
                      </GKButton>
                    </Grid2>
                    <Grid2 size={3}>
                      <GKButton
                        style={{
                          height: "100%",
                        }}
                        variant={
                          field.value === "custom" ? "contained" : "outlined"
                        }
                        fullWidth
                        onClick={() => handleClickPeriods("custom")}
                      >
                        Custom
                      </GKButton>
                    </Grid2>
                  </Grid2>
                )}
              />
              {getValues("currentOption") === "custom" && (
                <Grid2 size={12} mt={1} display={"flex"} gap={2}>
                  <Controller
                    name="from_date"
                    control={form.control}
                    render={({ field }) => (
                      <GKDatePicker
                        {...field}
                        inputLabel="Start Date"
                        value={dayjs(field.value)}
                        onChange={(value: any) =>
                          field.onChange(dayjs(value).format("YYYY-MM-DD"))
                        }
                        disableFuture
                        slotProps={{
                          textField: {
                            error: false,
                            helperText: false,
                          },
                        }}
                      />
                    )}
                  />

                  <Controller
                    name="to_date"
                    control={form.control}
                    render={({ field }) => (
                      <GKDatePicker
                        {...field}
                        inputLabel="End Date"
                        value={dayjs(field.value)}
                        minDate={
                          dayjs(field.value).isValid()
                            ? dayjs(field.value)
                            : null
                        }
                        onChange={(value: any) =>
                          field.onChange(dayjs(value).format("YYYY-MM-DD"))
                        }
                        disableFuture
                        slotProps={{
                          textField: {
                            error: false,
                            helperText: false,
                          },
                        }}
                      />
                    )}
                  />
                </Grid2>
              )}
            </>
          )}

          {(selectedReport?.reportName === "year_on_year_performance_report" ||
            selectedReport?.reportName === "gst_report") && (
            <>
              <Grid2 container size={6}>
                <Controller
                  name="from_date"
                  control={form.control}
                  render={({ field, fieldState: { error } }) => (
                    <GKDatePicker
                      {...field}
                      inputLabel="Start Date"
                      value={dayjs(field.value)}
                      onChange={(value) =>
                        field.onChange(
                          value ? dayjs(value).format("YYYY-MM-DD") : "",
                        )
                      }
                      disableFuture
                      slotProps={{
                        textField: {
                          error: !!error,
                          helperText: error?.message,
                        },
                      }}
                    />
                  )}
                />
              </Grid2>
              <Grid2 container size={6}>
                <Controller
                  name="to_date"
                  control={form.control}
                  render={({ field, fieldState: { error } }) => (
                    <GKDatePicker
                      {...field}
                      inputLabel="End Date"
                      value={dayjs(field.value)}
                      onChange={(value) =>
                        field.onChange(
                          value ? dayjs(value).format("YYYY-MM-DD") : "",
                        )
                      }
                      maxDate={dayjs()}
                      disableFuture
                      slotProps={{
                        textField: {
                          error: !!error,
                          helperText: error?.message,
                        },
                      }}
                    />
                  )}
                />
              </Grid2>
            </>
          )}

          {selectedReport?.reportName === "year_on_year_performance_report" && (
            <>
              <Grid2 size={12}>
                <Controller
                  name="benchmark"
                  control={form.control}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <GKSearchSelect
                        {...field}
                        disableClearable
                        inputLabel="Benchmark"
                        loading={benchmarkLoading}
                        onChange={(event, val) => field.onChange([val?.value])}
                        value={
                          benchmarkList?.filter((option: { value: string }) =>
                            field.value.includes(option.value),
                          )[0]?.name || []
                        }
                        options={benchmarkList || []}
                        getOptionLabel={(option) =>
                          isArray(option) ? "" : option.name || option
                        }
                        error={!!error}
                        helperText={error?.message}
                      />
                    );
                  }}
                />
              </Grid2>
              <Grid2 size={12}>
                <Controller
                  name="returnMetrics"
                  control={form.control}
                  render={({ field }) => {
                    return (
                      <FormControl
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <Typography
                          color={"#4B5565"}
                          display={"flex"}
                          alignItems={"center"}
                          gap={"4px"}
                        >
                          Return Metrics
                          <Tooltip
                            arrow
                            title={
                              <Typography fontSize={12}>
                                <li>
                                  XIRR: Even if the investment is less than one
                                  year, XIRR is displayed.
                                </li>
                                <li>
                                  TWRR: For investments under one year, absolute
                                  return is shown; for more than a year, TWRR is
                                  applied.
                                </li>
                              </Typography>
                            }
                            placement="right"
                          >
                            <IconButton>
                              <ErrorfilledIcon width={16} height={16} />
                            </IconButton>
                          </Tooltip>
                        </Typography>
                        <RadioGroup row {...field} defaultValue={field.value}>
                          {CUSTOM_REPORT_RETURN_METRICS.map((dataItem) => (
                            <FormControlLabel
                              value={dataItem.value}
                              control={<GKRadio />}
                              label={dataItem.name}
                            />
                          ))}
                        </RadioGroup>
                      </FormControl>
                    );
                  }}
                />
              </Grid2>
            </>
          )}

          {(selectedReport?.reportName === "xirr_report" ||
            selectedReport?.reportName === "historical_valuation_report" ||
            selectedReport?.reportName ===
              "unrealized_capital_gain_detailed_report" ||
            selectedReport?.reportName ===
              "unrealized_capital_gain_summary_report" ||
            (selectedReport?.reportType === "template" &&
              selectedReport?.reportName !==
                "benchmark_performance_report")) && (
            <Grid2 size={12}>
              <Controller
                name="end_date"
                control={form.control}
                render={({ field, fieldState: { error } }) => (
                  <GKDatePicker
                    {...field}
                    inputLabel="Date of Generation"
                    value={dayjs(field.value)}
                    onChange={(value) =>
                      field.onChange(dayjs(value).format("YYYY-MM-DD"))
                    }
                    disableFuture
                    slotProps={{
                      textField: {
                        error: !!error,
                        helperText: error?.message,
                      },
                    }}
                  />
                )}
              />
            </Grid2>
          )}

          <Show.When
            isTrue={
              selectedReport?.reportName === "benchmark_performance_report" &&
              selectedReport?.reportType === "template"
            }
          >
            <Grid2 size={12}>
              <Controller
                name="portfolio"
                control={form.control}
                render={({ field, formState: { errors } }) => (
                  <GKSearchSelect
                    {...field}
                    disabled={selectedIndex.loading}
                    inputLabel={(() => {
                      const reportName = form.getValues(
                        "selectedReported.reportName",
                      );
                      const selectType = form.getValues(
                        "selectedReported.customData.selectType",
                      );

                      if (reportName === "benchmark_performance_report") {
                        if (selectType === "portfolio") return "Portfolio";
                        if (selectType === "client") return "Client";
                        if (selectType === "family") return "Family";
                      }

                      return "Portfolio, Client or Family";
                    })()}
                    multiple
                    loading={isInitialLoading}
                    onChange={(event, val) => {
                      field.onChange(val);
                    }}
                    groupBy={(option: any) => option.type?.toUpperCase()}
                    renderOption={(props, option) => {
                      return (
                        <li
                          {...props}
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                          }}
                        >
                          <Typography fontWeight={600}>
                            {option.clientName}
                          </Typography>
                          <Typography>{option.name}</Typography>
                        </li>
                      );
                    }}
                    options={
                      portfolioList?.data
                        // ?.filter((item: { type: string }) =>
                        //   selectedReport?.reportName ===
                        //   "benchmark_performance_report"
                        //     ? item?.type !== "family"
                        //     : item,
                        // )
                        ?.filter((item: { type: string }) => {
                          const reportName = form.getValues(
                            "selectedReported.reportName",
                          );
                          const selectType = form.getValues(
                            "selectedReported.customData.selectType",
                          );

                          if (reportName === "benchmark_performance_report") {
                            if (selectType === "portfolio") {
                              return item?.type === "portfolio";
                            }
                            if (selectType === "client") {
                              return item?.type === "client";
                            }
                            if (selectType === "family") {
                              return item?.type === "family";
                            }
                          }

                          return item;
                        })
                        ?.map((dataItem: any) => {
                          return {
                            ...dataItem,
                            name: `${dataItem.name} - ${dataItem.code}`,
                            value: dataItem.id,
                          };
                        }) || []
                    }
                    getOptionLabel={(option) => option.name || option}
                    error={!!errors?.portfolio}
                    helperText={errors?.portfolio?.message as string}
                  />
                )}
              />
            </Grid2>
            <>
              <Controller
                name="currentOption"
                control={form.control}
                render={({ field }) => (
                  <Grid2 container size={12} display={"flex"} spacing={1}>
                    <Grid2 size={12}>
                      <Typography color={"var(--black)"}>
                        Available Time Periods
                      </Typography>
                    </Grid2>
                    <Grid2 size={3}>
                      <GKButton
                        style={{
                          height: "100%",
                        }}
                        fullWidth
                        variant={
                          field.value === "inception" ? "contained" : "outlined"
                        }
                        onClick={() => handleClickPeriods("inception")}
                      >
                        Since Inception
                      </GKButton>
                    </Grid2>
                    <Grid2 size={3}>
                      <GKButton
                        style={{
                          height: "100%",
                        }}
                        fullWidth
                        variant={
                          field.value === "thisFY" ? "contained" : "outlined"
                        }
                        onClick={() => handleClickPeriods("thisFY")}
                      >
                        This FY
                      </GKButton>
                    </Grid2>
                    <Grid2 size={3}>
                      <GKButton
                        style={{
                          height: "100%",
                        }}
                        fullWidth
                        variant={
                          field.value === "lastFY" ? "contained" : "outlined"
                        }
                        onClick={() => handleClickPeriods("lastFY")}
                      >
                        Previous FY
                      </GKButton>
                    </Grid2>
                    <Grid2 size={3}>
                      <GKButton
                        style={{
                          height: "100%",
                        }}
                        variant={
                          field.value === "custom" ? "contained" : "outlined"
                        }
                        fullWidth
                        onClick={() => handleClickPeriods("custom")}
                      >
                        Custom
                      </GKButton>
                    </Grid2>
                  </Grid2>
                )}
              />
              {getValues("currentOption") === "custom" && (
                <Grid2 size={12} mt={1} display={"flex"} gap={2}>
                  <Controller
                    name="from_date"
                    control={form.control}
                    render={({ field }) => (
                      <GKDatePicker
                        {...field}
                        inputLabel="Start Date"
                        value={dayjs(field.value)}
                        onChange={(value: any) =>
                          field.onChange(dayjs(value).format("YYYY-MM-DD"))
                        }
                        disableFuture
                        slotProps={{
                          textField: {
                            error: false,
                            helperText: false,
                          },
                        }}
                      />
                    )}
                  />

                  <Controller
                    name="to_date"
                    control={form.control}
                    render={({ field }) => (
                      <GKDatePicker
                        {...field}
                        inputLabel="End Date"
                        value={dayjs(field.value)}
                        minDate={
                          dayjs(field.value).isValid()
                            ? dayjs(field.value)
                            : null
                        }
                        onChange={(value: any) =>
                          field.onChange(dayjs(value).format("YYYY-MM-DD"))
                        }
                        disableFuture
                        slotProps={{
                          textField: {
                            error: false,
                            helperText: false,
                          },
                        }}
                      />
                    )}
                  />
                </Grid2>
              )}
            </>
          </Show.When>

          {selectedReport?.reportName === "custom_report" &&
            selectedReport?.reportType === "template" &&
            (getValues(
              "selectedReported.customData.availableTimePeriods",
            )?.includes("custom_date_range") ||
              getValues(
                "selectedReported.customData.benchmarkComparisionTimePeriods",
              )?.includes("custom_date_range")) && (
              <>
                <Grid2 container size={6}>
                  <Controller
                    name="custom_date.start_date"
                    control={form.control}
                    render={({ field }) => (
                      <GKDatePicker
                        {...field}
                        inputLabel="Start Date"
                        value={dayjs(field.value)}
                        onChange={(value) =>
                          field.onChange(
                            value ? dayjs(value).format("YYYY-MM-DD") : "",
                          )
                        }
                        disableFuture
                        slotProps={{
                          textField: {
                            error: false,
                            helperText: false,
                          },
                        }}
                      />
                    )}
                  />
                </Grid2>
                <Grid2 container size={6}>
                  <Controller
                    name="custom_date.end_date"
                    control={form.control}
                    render={({ field }) => (
                      <GKDatePicker
                        {...field}
                        inputLabel="End Date"
                        value={dayjs(field.value)}
                        onChange={(value) =>
                          field.onChange(
                            value ? dayjs(value).format("YYYY-MM-DD") : "",
                          )
                        }
                        maxDate={dayjs()}
                        disableFuture
                        slotProps={{
                          textField: {
                            error: false,
                            helperText: false,
                          },
                        }}
                      />
                    )}
                  />
                </Grid2>
              </>
            )}

          <Show.When
            isTrue={
              selectedReport?.reportType !== "template" &&
              (selectedReport?.reportName === "custom_report" ||
                selectedReport?.reportName === "benchmark_performance_report")
            }
          >
            <CustomReportForm
              benchmarkList={benchmarkList}
              benchmarkLoading={benchmarkLoading}
              quoteList={quoteList}
              handleDeleteQuote={handleDeleteQuote}
              generateQuote={generateQuote}
              setEditQuoteValue={setEditQuoteValue}
              setOpenQuoteModal={setOpenQuoteModal}
            />
          </Show.When>

          <Show.When
            isTrue={selectedReport?.reportName === "stock_wise_holdings_report"}
          >
            <Grid2 size={12}>
              <Controller
                name={"gcode"}
                control={form.control}
                render={({ field, formState: { errors } }) => (
                  <GKSearchSelect
                    {...field}
                    disableClearable
                    inputLabel="Company"
                    onChange={(event, val) => {
                      field.onChange(val?.value);
                    }}
                    value={
                      companies.find((data: any) => data?.value === field.value)
                        ?.name || []
                    }
                    options={companies || []}
                    getOptionLabel={(option) =>
                      Array.isArray(option) ? "" : option.name || option
                    }
                    error={!!errors?.gcode}
                    helperText={errors?.gcode?.message as string}
                  />
                )}
              />
            </Grid2>
            <Grid2 size={12}>
              <Controller
                name={"to_date"}
                control={form.control}
                render={({ field, formState: { errors } }) => (
                  <GKDatePicker
                    inputLabel="Date"
                    name="to_date"
                    value={dayjs(field.value)}
                    onChange={(value: any) =>
                      field.onChange(dayjs(value).format("YYYY-MM-DD"))
                    }
                    disableFuture
                    slotProps={{
                      textField: {
                        error: !!errors?.to_date,
                        helperText: errors?.to_date?.message as string,
                      },
                    }}
                  />
                )}
              />
            </Grid2>
          </Show.When>

          <Grid2 size={12}>
            <Divider />
          </Grid2>
          <Show.When isTrue={selectedReport?.reportName === "gst_report"}>
            <Grid2 size={6}>
              <Controller
                name="equityBasket"
                control={form.control}
                rules={{
                  required: "Basket is required",
                }}
                render={({ field, fieldState: { error } }) => (
                  <GKSearchSelect
                    multiple
                    inputLabel="Select Basket"
                    requiredField
                    options={equityBasketOptions}
                    value={equityBasketOptions.filter((o: any) =>
                      Array.isArray(field.value)
                        ? field.value.includes(o.id)
                        : false,
                    )}
                    onChange={(_, selected) => {
                      field.onChange(selected.map((item: any) => item.id));
                    }}
                    getOptionLabel={(option) => option?.name || ""}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    error={!!error}
                    helperText={error?.message}
                    filterSelectedOptions={false}
                  />
                )}
              />
            </Grid2>
          </Show.When>

          <Show.When
            isTrue={(() => {
              const reportName = selectedReport?.reportName;

              if (
                !FORMAT_ALLOWED_REPORTS.includes(reportName) &&
                reportName !== "aum_growth_report"
              ) {
                return false;
              }
              const list = form.watch("portfolio") || [];
              const hasClient = list.some((i: any) => i?.type === "client");
              const hasFamily = list.some((i: any) => i?.type === "family");
              const hasPortfolio = list.some(
                (i: any) => i?.type === "portfolio",
              );

              if (
                hasFamily &&
                (selectedReport?.reportName ===
                  "unrealized_capital_gain_summary_report" ||
                  selectedReport?.reportName ===
                    "unrealized_capital_gain_detailed_report" ||
                  selectedReport?.reportName ===
                    "capital_gain_detailed_report" ||
                  selectedReport?.reportName === "capital_gain_summary_report")
              ) {
                return false;
              }

              return !hasPortfolio && hasClient !== hasFamily;
            })()}
          >
            <Grid2 size={4}>
              <Controller
                name="selectFormat"
                control={form.control}
                defaultValue="individual"
                render={({ field }) => (
                  <GKSelect {...field} inputLabel="Select Format">
                    {" "}
                    <MenuItem value="individual">Individual</MenuItem>{" "}
                    <MenuItem value="zip">ZIP</MenuItem>{" "}
                  </GKSelect>
                )}
              />{" "}
            </Grid2>
          </Show.When>

          <Show.When
            isTrue={
              (selectedReport?.reportName !== "custom_report" &&
                selectedReport?.reportName !==
                  "benchmark_performance_report") ||
              selectedReport?.reportType === "template"
            }
          >
            <Grid2 size={12} display={"flex"} gap={1}>
              <GKButton
                variant="contained"
                loading={selectedIndex.index === 0 && selectedIndex.loading}
                form="report-form"
                type="submit"
                disabled={
                  selectedIndex.loading ||
                  ((selectedReport?.reportName === "custom_report" ||
                    selectedReport?.reportName ===
                      "benchmark_performance_report") &&
                    selectedReport?.reportType !== "template") ||
                  selectedReport?.reportName === "gst_report"
                }
                onClick={() =>
                  setSelectedIndex((prev: any) => {
                    return {
                      ...prev,
                      index: 0,
                    };
                  })
                }
              >
                {`Generate PDF`}
              </GKButton>
              <GKButton
                variant="contained"
                type="submit"
                form="report-form"
                onClick={() =>
                  setSelectedIndex((prev: any) => {
                    return {
                      ...prev,
                      index: 1,
                    };
                  })
                }
                disabled={
                  selectedReport?.reportName === "custom_report" ||
                  selectedReport?.reportName ===
                    "portfolio_performance_report" ||
                  selectedReport?.reportName === "stock_wise_holdings_report" ||
                  selectedReport?.reportName ===
                    "benchmark_performance_report" ||
                  selectedIndex.loading
                }
                loading={selectedIndex.index === 1 && selectedIndex.loading}
              >
                {`Generate EXCEL`}
              </GKButton>
            </Grid2>
          </Show.When>

          <Show.When
            isTrue={
              (selectedReport?.reportName === "custom_report" ||
                selectedReport?.reportName ===
                  "benchmark_performance_report") &&
              selectedReport?.reportType !== "template"
            }
          >
            <Grid2 size={12}>
              <GKButton
                variant="contained"
                onClick={async () => {
                  const isValid = await form.trigger(["benchmark"]);

                  if (isValid) setOpenTemplateModal(true);
                }}
              >
                save as template
              </GKButton>
            </Grid2>
          </Show.When>

          <Grid2 size={12}>
            <Divider />
          </Grid2>

          <CustomReportPreview benchmarkList={benchmarkList} />
        </Grid2>
      </Show.When>

      <Show.When isTrue={openPdfView}>
        <ViewPdf
          open={openPdfView}
          setOpen={setOpenPdfView}
          file={selectedReport?.sampleFile}
        />
      </Show.When>

      <Show.When isTrue={openTemplateModal}>
        <TemplateModal
          open={openTemplateModal}
          setOpen={setOpenTemplateModal}
        />
      </Show.When>
    </>
  );
};

export default GenerateReportForm;
